import React, { useEffect, useMemo, useRef, useState } from 'react';
import './MediaViewer.scss';

function detectMediaType(link, type) {
    const normalizedType = String(type || '').toLowerCase();
    if (['image', 'video', 'audio', 'file'].includes(normalizedType)) {
        return normalizedType;
    }

    const extension = String(link || '').split('?')[0].split('.').pop()?.toLowerCase() || '';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(extension)) return 'image';
    if (['mp4', 'webm', 'ogg', 'ogv', 'mov'].includes(extension)) return 'video';
    if (['mp3', 'wav', 'oga', 'm4a'].includes(extension)) return 'audio';
    return 'file';
}

export default function MediaViewer({ link, type, alt = 'Вложение', label = '' }) {
    const resolvedType = useMemo(() => detectMediaType(link, type), [link, type]);
    const [isOpen, setIsOpen] = useState(false);
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [dragState, setDragState] = useState(null);
    const imageRef = useRef(null);

    useEffect(() => {
        if (!isOpen) {
            return;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                setIsOpen(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen]);

    useEffect(() => {
        if (scale <= 1 && (offset.x !== 0 || offset.y !== 0)) {
            setOffset({ x: 0, y: 0 });
        }
    }, [scale, offset.x, offset.y]);

    if (!link) {
        return null;
    }

    const resetImageTransform = () => {
        setScale(1);
        setOffset({ x: 0, y: 0 });
    };

    const openModal = () => {
        resetImageTransform();
        setIsOpen(true);
    };

    const handleWheel = (event) => {
        if (resolvedType !== 'image') {
            return;
        }

        event.preventDefault();
        const delta = event.deltaY > 0 ? -0.25 : 0.25;
        setScale((prev) => {
            const next = Math.min(4, Math.max(1, Number((prev + delta).toFixed(2))));
            if (next === 1) {
                setOffset({ x: 0, y: 0 });
            }
            return next;
        });
    };

    const handleMouseDown = (event) => {
        if (resolvedType !== 'image' || scale <= 1) {
            return;
        }

        setDragState({
            startX: event.clientX,
            startY: event.clientY,
            offsetX: offset.x,
            offsetY: offset.y,
        });
    };

    const handleMouseMove = (event) => {
        if (!dragState || resolvedType !== 'image' || scale <= 1) {
            return;
        }

        setOffset({
            x: dragState.offsetX + (event.clientX - dragState.startX),
            y: dragState.offsetY + (event.clientY - dragState.startY),
        });
    };

    const handleDoubleClick = () => {
        if (resolvedType !== 'image') {
            return;
        }

        setScale((prev) => {
            if (prev === 1) {
                return 2;
            }

            setOffset({ x: 0, y: 0 });
            return 1;
        });
    };

    const preview = (() => {
        if (resolvedType === 'image') {
            return (
                <button type="button" className="mediaViewer__thumbBtn" onClick={openModal}>
                    <img src={link} alt={alt} className="mediaViewer__thumb" loading="lazy" />
                </button>
            );
        }

        if (resolvedType === 'video') {
            return (
                <button type="button" className="mediaViewer__previewCard" onClick={openModal}>
                    <video src={link} className="mediaViewer__videoPreview" muted playsInline preload="metadata" />
                    <span className="mediaViewer__previewLabel">{label || 'Открыть видео'}</span>
                </button>
            );
        }

        if (resolvedType === 'audio') {
            return (
                <button type="button" className="mediaViewer__previewCard" onClick={openModal}>
                    <span className="mediaViewer__previewLabel">{label || 'Открыть аудио'}</span>
                </button>
            );
        }

        return (
            <a href={link} className="mediaViewer__fileLink" target="_blank" rel="noreferrer">
                {label || 'Скачать файл'}
            </a>
        );
    })();

    return (
        <div className="mediaViewer">
            {preview}

            {isOpen ? (
                <div
                    className="mediaViewer__overlay"
                    onClick={() => setIsOpen(false)}
                    role="dialog"
                    aria-modal="true"
                >
                    <button
                        type="button"
                        className="mediaViewer__close"
                        onClick={() => setIsOpen(false)}
                        aria-label="Закрыть"
                    >
                        ×
                    </button>

                    <div
                        className="mediaViewer__content"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="mediaViewer__toolbar">
                            {resolvedType === 'image' ? (
                                <>
                                    <button type="button" onClick={() => setScale((prev) => Math.min(4, prev + 0.25))}>+</button>
                                    <button type="button" onClick={() => setScale((prev) => Math.max(1, prev - 0.25))}>-</button>
                                    <button type="button" onClick={resetImageTransform}>Сброс</button>
                                </>
                            ) : null}
                            <a href={link} target="_blank" rel="noreferrer">Скачать</a>
                        </div>

                        {resolvedType === 'image' ? (
                            <div
                                className="mediaViewer__stage"
                                onWheel={handleWheel}
                                onMouseMove={handleMouseMove}
                                onMouseUp={() => setDragState(null)}
                                onMouseLeave={() => setDragState(null)}
                            >
                                <img
                                    ref={imageRef}
                                    src={link}
                                    alt={alt}
                                    className="mediaViewer__image"
                                    onDoubleClick={handleDoubleClick}
                                    onMouseDown={handleMouseDown}
                                    style={{
                                        transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                                        cursor: scale > 1 ? (dragState ? 'grabbing' : 'grab') : 'zoom-in',
                                    }}
                                />
                            </div>
                        ) : null}

                        {resolvedType === 'video' ? (
                            <video
                                src={link}
                                className="mediaViewer__video"
                                controls
                                playsInline
                                preload="metadata"
                            />
                        ) : null}

                        {resolvedType === 'audio' ? (
                            <audio src={link} className="mediaViewer__audio" controls preload="metadata" />
                        ) : null}
                    </div>
                </div>
            ) : null}
        </div>
    );
}
